import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { AnimatedNumber } from '../motion';

export interface StatCardProps {
  /** Small descriptive label (e.g. "Matching"). */
  label: React.ReactNode;
  /** The numeric or short text value rendered prominently. */
  value: React.ReactNode;
  /** Optional icon shown above/beside the value. */
  icon?: React.ReactNode;
  /**
   * Optional `Intl.NumberFormatOptions` applied when `value` is a number, so
   * the animated ticker formats it (currency/compact/percent/etc.) exactly as
   * the caller intends.
   */
  format?: Intl.NumberFormatOptions;
}

/**
 * Single stat cell: label (muted) + value (display typography).
 *
 * Designed to be composed inside a {@link StatStrip}, but also works
 * standalone. Visuals follow spec §3.3 (StateSpin Articles strip).
 */
export const StatCard: React.FC<StatCardProps> = ({ label, value, icon, format }) => {
  // When the value is a plain finite number, route it through the animated
  // ticker (NumberFlow) so it counts up/changes smoothly; otherwise render the
  // node (string / element) verbatim.
  const renderedValue =
    typeof value === 'number' && Number.isFinite(value) ? (
      <AnimatedNumber value={value} format={format} />
    ) : (
      value
    );
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 2,
        py: 1.5,
        minWidth: 120,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        // Transparent so it sits cleanly inside a horizontal strip
        // without double-outlining when used inline.
        backgroundColor: 'transparent',
        border: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {icon && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>
        )}
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="display" component="div">
        {renderedValue}
      </Typography>
    </Paper>
  );
};

export default StatCard;
